import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

/**
 * How long a text/numeric settings draft waits after the last keystroke
 * before it is applied to the layout engine.
 *
 * Discrete controls (checkbox, select, resize handle, breakpoint add/remove)
 * are never debounced — they apply on the spot. Only free-text and numeric
 * drafts go through this delay so a 12 → 24 column edit never applies the
 * destructive intermediate `2`, and a placeholder-text edit never remounts
 * GridStack once per character.
 *
 * Enter and blur bypass the delay entirely: controllers cancel the pending
 * key and commit synchronously.
 */
export const DRAFT_COMMIT_DELAY_MS = 300;

export interface DraftCommitScheduler<K> {
  /**
   * Arms (or re-arms) the debounced commit for `key`.
   *
   * Scheduling a *different* key commits the previously pending one
   * synchronously first, so moving between fields can never silently drop an
   * in-flight draft.
   */
  schedule: (key: K) => void;
  /**
   * Drops the pending draft without committing it.
   *
   * With no argument, drops whatever is pending (Restart, unmount). With a
   * key, drops it only when that key is the pending one — which is what lets
   * an Enter/blur commit disarm its own timer without disturbing another
   * field's.
   *
   * Returns whether something was actually dropped.
   */
  cancel: (key?: K) => boolean;
}

/**
 * Owns the single pending-draft timer for a settings controller.
 *
 * The timer lives here rather than in component state so a pending draft can
 * never apply stale configuration: the pending key is dropped on `cancel()`
 * (used by Restart) and on unmount (used by tab switches). `onCommit` receives
 * only the key — callers recompute the value from their own latest state — so
 * this hook never holds a config snapshot that could go stale.
 */
export const useDraftCommitScheduler = <K>(
  onCommit: (key: K) => void,
): DraftCommitScheduler<K> => {
  const pendingRef = useRef<{ key: K } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Kept in a ref so an inline `onCommit` never re-arms or restarts the timer.
  const onCommitRef = useRef(onCommit);
  useLayoutEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) {
      onCommitRef.current(pending.key);
    }
  }, []);

  const schedule = useCallback(
    (key: K) => {
      const pending = pendingRef.current;
      if (pending && !Object.is(pending.key, key)) {
        clearTimer();
        runPending();
      }
      pendingRef.current = { key };
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        runPending();
      }, DRAFT_COMMIT_DELAY_MS);
    },
    [clearTimer, runPending],
  );

  const cancel = useCallback(
    (key?: K) => {
      const pending = pendingRef.current;
      if (!pending) {
        return false;
      }
      if (key !== undefined && !Object.is(pending.key, key)) {
        return false;
      }
      clearTimer();
      pendingRef.current = null;
      return true;
    },
    [clearTimer],
  );

  // Unmount must not leave a timer that would apply settings to a torn-down
  // engine (tab switch away from Layout Labs).
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  // Stable object identity so controllers can list the scheduler in their own
  // `useCallback` dependencies without invalidating their handlers each render.
  return useMemo(() => ({ schedule, cancel }), [schedule, cancel]);
};
